/**
 * Errors Dashboard Component
 *
 * Admin dashboard for viewing and analyzing frontend errors.
 * Displays error statistics, recent errors, and allows filtering.
 *
 * @version 2.0
 * @created 2025-11-19
 * @updated 2025-11-20 - Added sorting, pagination, status updates, error numbers
 */

import React, { useState, useEffect } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import DashboardLayout from '../layout/DashboardLayout';
import apiRequest from '../../services/api';
import StatusBadge from '../common/StatusBadge';
import Badge from '../common/Badge';
import TabGroup from '../common/TabGroup';
import EmptyState from '../common/EmptyState';
import Modal from '../common/Modal';

/**
 * Errors Dashboard Component
 *
 * Features:
 * - Error statistics (frontend vs backend/API errors)
 * - Recent errors table with tab-based filtering
 * - Error type breakdown
 * - Environment filtering
 * - Error detail modal
 */
export default function ErrorsDashboard() {
  const navigate = useNavigate();
  const { stats, errors } = useLoaderData();
  const [selectedError, setSelectedError] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'frontend', 'api'
  const [filter, setFilter] = useState({
    type: 'all',
    environment: 'all',
    status: 'all',
  });

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  // Sorting state
  const [sortField, setSortField] = useState('server_timestamp');
  const [sortDirection, setSortDirection] = useState('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Status change modal state
  const [statusChangeError, setStatusChangeError] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [_error, setError] = useState(null);

  // Categorize errors
  const apiErrorTypes = ['api_error'];
  const frontendErrorTypes = ['uncaught_error', 'route_error', 'unhandled_rejection'];

  // Calculate statistics
  const apiErrors = errors.errors.filter(e => apiErrorTypes.includes(e.type));
  const frontendErrors = errors.errors.filter(e => frontendErrorTypes.includes(e.type));

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Truncate message
  const truncateMessage = (message, maxLength = 60) => {
    if (!message || message.length <= maxLength) {return message;}
    return message.substring(0, maxLength) + '...';
  };

  // Get error type badge color for Badge component
  const getErrorTypeBadgeColor = (type) => {
    const colors = {
      uncaught_error: 'red',
      route_error: 'orange',
      api_error: 'yellow',
      unhandled_rejection: 'purple',
    };
    // eslint-disable-next-line security/detect-object-injection -- safe: type is enum from database
    return colors[type] || 'gray';
  };

  // Get environment badge color for Badge component
  const getEnvironmentBadgeColor = (env) => {
    const colors = {
      production: 'red',
      staging: 'yellow',
      development: 'green',
    };
    // eslint-disable-next-line security/detect-object-injection -- safe: env is enum from database
    return colors[env] || 'gray';
  };

  // Get status badge variant for StatusBadge component
  const getStatusBadgeVariant = (status) => {
    const variants = {
      logged: 'neutral',
      investigating: 'info',
      verifying_fix: 'info',
      resolved: 'success',
    };
    // eslint-disable-next-line security/detect-object-injection -- safe: status is enum from database
    return variants[status] || 'neutral';
  };

  // Handle status badge click
  const handleStatusClick = (error) => {
    setStatusChangeError(error);
    setNewStatus(error.status || 'logged');
  };

  // Handle status update
  const handleStatusUpdate = async () => {
    if (!statusChangeError || !newStatus) {return;}

    setIsUpdatingStatus(true);
    try {
      console.log('Updating error:', statusChangeError.id, 'to status:', newStatus);

      const response = await apiRequest(
        `/api/v1/errors/${statusChangeError.id}/status`,
        {
          method: 'PATCH',
          body: JSON.stringify({ status: newStatus }),
        }
      );

      console.log('Response:', response);

      // Update local state
      errors.errors = errors.errors.map((err) =>
        err.id === statusChangeError.id ? { ...err, status: newStatus } : err
      );

      setStatusChangeError(null);
      setNewStatus('');

      // Reload page to reflect changes
      window.location.reload();
    } catch (error) {
      console.error('Error updating status:', error);

      if (error.status === 401) {
        setError('Session expired. Please log in again.');
      setTimeout(() => setError(null), 3000);
      } else {
        setError(`Failed to update error status. Please try again. Error: ${error.message}`);
      setTimeout(() => setError(null), 3000);
      }
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default descending
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 ml-1 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-1 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Filter errors based on tab and filters
  const getFilteredErrors = () => {
    let filtered = errors.errors;

    // Tab filtering
    if (activeTab === 'frontend') {
      filtered = filtered.filter(e => frontendErrorTypes.includes(e.type));
    } else if (activeTab === 'api') {
      filtered = filtered.filter(e => apiErrorTypes.includes(e.type));
    }

    // Type filtering
    if (filter.type !== 'all') {
      filtered = filtered.filter(e => e.type === filter.type);
    }

    // Environment filtering
    if (filter.environment !== 'all') {
      filtered = filtered.filter(e => e.environment === filter.environment);
    }

    // Status filtering
    if (filter.status !== 'all') {
      filtered = filtered.filter(e => e.status === filter.status);
    }

    return filtered;
  };

  const filteredErrors = getFilteredErrors();

  // Sort filtered errors
  const sortedErrors = [...filteredErrors].sort((a, b) => {
    // eslint-disable-next-line security/detect-object-injection -- safe: sortField is controlled field name from UI dropdown
    let aVal = a[sortField];
    // eslint-disable-next-line security/detect-object-injection -- safe: sortField is controlled field name from UI dropdown
    let bVal = b[sortField];

    // Handle null/undefined values
    if (aVal === null || aVal === undefined) {aVal = '';}
    if (bVal === null || bVal === undefined) {bVal = '';}

    // Convert to lowercase for string comparison
    if (typeof aVal === 'string') {aVal = aVal.toLowerCase();}
    if (typeof bVal === 'string') {bVal = bVal.toLowerCase();}

    // Compare
    if (aVal < bVal) {return sortDirection === 'asc' ? -1 : 1;}
    if (aVal > bVal) {return sortDirection === 'asc' ? 1 : -1;}
    return 0;
  });

  // Paginate sorted errors
  const totalPages = Math.ceil(sortedErrors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedErrors = sortedErrors.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, activeTab]);

  // Test error function
  const throwTestError = () => {
    throw new Error('Test error: This is a deliberate error to test error logging functionality');
  };

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="errors"
      onNavigate={handleNavigate}
      user={{ name: 'Admin User', email: 'admin@outdoorguides.com', avatar: null }}
    >
      {/* Page Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-primary)', margin: 0 }}>
          Error Monitoring
        </h1>
      </div>

      {/* Filters at the top */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <div>
            <label
              htmlFor="error-type"
              className="block text-sm font-medium text-gray-700"
            >
              Error Type
            </label>
            <select
              id="error-type"
              value={filter.type}
              onChange={(e) => setFilter({ ...filter, type: e.target.value })}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-rose-500 focus:border-rose-500 sm:text-sm rounded-md"
            >
              <option value="all">All Types</option>
              <option value="uncaught_error">Uncaught Error</option>
              <option value="route_error">Route Error</option>
              <option value="api_error">API Error</option>
              <option value="unhandled_rejection">Unhandled Rejection</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="environment"
              className="block text-sm font-medium text-gray-700"
            >
              Environment
            </label>
            <select
              id="environment"
              value={filter.environment}
              onChange={(e) =>
                setFilter({ ...filter, environment: e.target.value })
              }
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-rose-500 focus:border-rose-500 sm:text-sm rounded-md"
            >
              <option value="all">All Environments</option>
              <option value="production">Production</option>
              <option value="staging">Staging</option>
              <option value="development">Development</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="status"
              className="block text-sm font-medium text-gray-700"
            >
              Status
            </label>
            <select
              id="status"
              value={filter.status}
              onChange={(e) =>
                setFilter({ ...filter, status: e.target.value })
              }
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-rose-500 focus:border-rose-500 sm:text-sm rounded-md"
            >
              <option value="all">All Statuses</option>
              <option value="logged">Logged</option>
              <option value="investigating">Investigating</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Frontend Errors
                  </dt>
                  <dd className="text-3xl font-semibold text-gray-900">
                    {frontendErrors.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-yellow-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    API Errors
                  </dt>
                  <dd className="text-3xl font-semibold text-gray-900">
                    {apiErrors.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Affected Users
                  </dt>
                  <dd className="text-3xl font-semibold text-gray-900">
                    {stats.affected_users}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Production Errors
                  </dt>
                  <dd className="text-3xl font-semibold text-red-600">
                    {stats.production_errors}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Errors Table with Tabs */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">
              Recent Errors ({filteredErrors.length})
            </h2>
          </div>

          {/* Tabs */}
          <div className="mt-4">
            <TabGroup
              tabs={[
                { id: 'all', label: 'All Errors', count: errors.errors.length },
                { id: 'frontend', label: 'Frontend', count: frontendErrors.length },
                { id: 'api', label: 'API', count: apiErrors.length },
              ]}
              activeTab={activeTab}
              onChange={setActiveTab}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('id')}
                    className="flex items-center hover:text-gray-700"
                  >
                    Error #
                    {getSortIcon('id')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('server_timestamp')}
                    className="flex items-center hover:text-gray-700"
                  >
                    Timestamp
                    {getSortIcon('server_timestamp')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('type')}
                    className="flex items-center hover:text-gray-700"
                  >
                    Type
                    {getSortIcon('type')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('message')}
                    className="flex items-center hover:text-gray-700"
                  >
                    Message
                    {getSortIcon('message')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center hover:text-gray-700"
                  >
                    Status
                    {getSortIcon('status')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('environment')}
                    className="flex items-center hover:text-gray-700"
                  >
                    Environment
                    {getSortIcon('environment')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('user_email')}
                    className="flex items-center hover:text-gray-700"
                  >
                    User
                    {getSortIcon('user_email')}
                  </button>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedErrors.map((error) => (
                <tr key={error.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">
                    #{error.id.substring(0, 8)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatTimestamp(error.server_timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge color={getErrorTypeBadgeColor(error.type)}>
                      {error.type.replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {truncateMessage(error.message)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleStatusClick(error)}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      title="Click to change status"
                    >
                      <StatusBadge variant={getStatusBadgeVariant(error.status || 'logged')}>
                        {(error.status || 'logged').replace(/_/g, ' ').charAt(0).toUpperCase() + (error.status || 'logged').replace(/_/g, ' ').slice(1)}
                      </StatusBadge>
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {error.environment ? (
                      <Badge color={getEnvironmentBadgeColor(error.environment)}>
                        {error.environment}
                      </Badge>
                    ) : (
                      <span className="text-gray-400 text-sm">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {error.user_email ? (
                      <span>{error.user_email}</span>
                    ) : (
                      <span className="text-gray-400">Anonymous</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => setSelectedError(error)}
                      className="text-rose-600 hover:text-rose-900"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredErrors.length === 0 && (
            <EmptyState
              icon={CheckCircle}
              title="No errors found"
              description="No errors match the selected filters."
            />
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, sortedErrors.length)}</span> of{' '}
                  <span className="font-medium">{sortedErrors.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  {[...Array(totalPages)].map((_, idx) => {
                    const pageNum = idx + 1;
                    // Show first page, last page, current page, and pages around current
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? 'z-10 bg-rose-50 border-rose-500 text-rose-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                      return (
                        <span
                          key={pageNum}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                        >
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Test Error Button at Bottom */}
      <div className="mt-6 flex justify-center">
        <button
          onClick={throwTestError}
          className="px-6 py-3 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors shadow-sm"
          title="Click to generate a test error"
        >
          Test Error Logging
        </button>
      </div>

      {/* Status Change Modal */}
      <Modal
        isOpen={statusChangeError !== null}
        onClose={() => setStatusChangeError(null)}
        title="Change Error Status"
        size="md"
        footer={
          <div className="grid grid-cols-2 gap-3 w-full">
            <button
              onClick={() => setStatusChangeError(null)}
              disabled={isUpdatingStatus}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleStatusUpdate}
              disabled={isUpdatingStatus || (statusChangeError && newStatus === statusChangeError.status)}
              className="px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-md hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdatingStatus ? 'Updating...' : 'Update Status'}
            </button>
          </div>
        }
      >
        {statusChangeError && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Error #{statusChangeError.id.substring(0, 8)}
              </label>
              <p className="text-sm text-gray-600 mb-4">
                {truncateMessage(statusChangeError.message, 100)}
              </p>
            </div>

            <div>
              <label htmlFor="new-status" className="block text-sm font-medium text-gray-700 mb-2">
                New Status
              </label>
              <select
                id="new-status"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-rose-500 focus:border-rose-500 sm:text-sm rounded-md"
              >
                <option value="logged">Logged</option>
                <option value="investigating">Investigating</option>
                <option value="verifying_fix">Verifying Fix</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>
        )}
      </Modal>

      {/* Error Detail Modal */}
      <Modal
        isOpen={selectedError !== null}
        onClose={() => setSelectedError(null)}
        title="Error Details"
        size="lg"
        footer={
          <button
            onClick={() => setSelectedError(null)}
            className="w-full px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-md hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500"
          >
            Close
          </button>
        }
      >
        {selectedError && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Error Type
              </label>
              <div className="mt-1">
                <Badge color={getErrorTypeBadgeColor(selectedError.type)}>
                  {selectedError.type.replace(/_/g, ' ')}
                </Badge>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Message
              </label>
              <p className="mt-1 text-sm text-gray-900">
                {selectedError.message}
              </p>
            </div>

            {selectedError.stack && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Stack Trace
                </label>
                <pre className="mt-1 text-xs text-gray-900 bg-gray-50 p-3 rounded overflow-x-auto">
                  {selectedError.stack}
                </pre>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Environment
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedError.environment || 'N/A'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  App Version
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedError.app_version || 'N/A'}
                </p>
              </div>
            </div>

            {selectedError.url && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Page URL
                </label>
                <p className="mt-1 text-sm text-gray-900 break-all">
                  {selectedError.url}
                </p>
              </div>
            )}

            {selectedError.user_agent && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  User Agent
                </label>
                <p className="mt-1 text-xs text-gray-900 break-all">
                  {selectedError.user_agent}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  User Email
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedError.user_email || 'Anonymous'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Timestamp
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(
                    selectedError.server_timestamp
                  ).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
}
